import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useStructurePick } from '../hooks/useStructurePick';
import { structureLabel, structureTypeLabel, formatDistance, } from '../utils/structureLabel';
import '../styles/node-move-panel.css';
const NEARBY_RADIUS = 150;
const NEARBY_LIMIT = 8;
const SNAP_RADIUS = 20;
const FAR_WARNING_METRES = 500;
const UNDO_SECONDS = 10;
const nodeName = (node) => node.node_cfn_id || node.node_description || `node #${node.id}`;
const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
const gridDistance = (a, b) => Math.hypot(a.easting - b.easting, a.northing - b.northing);
async function requestJson(url, init) {
    const response = await fetch(url, {
        ...init,
        headers: { Accept: 'application/json', ...(init?.headers || {}) },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || `Request failed (${response.status})`);
    }
    return body;
}
const moveNode = (nodeId, structureId) => requestJson(`/agg_node/move/${nodeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ structure_id: structureId }),
});
export const NodeMovePanel = ({ map, node, fromStructureId, onMoved, onClose, }) => {
    const [current, setCurrent] = useState(null);
    const [nearby, setNearby] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [finding, setFinding] = useState(false);
    const [moving, setMoving] = useState(false);
    const [moved, setMoved] = useState(null);
    const [undoState, setUndoState] = useState('idle');
    const [secondsLeft, setSecondsLeft] = useState(UNDO_SECONDS);
    const layerRef = useRef(null);
    // Load the node's current structure, then what is near it.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const found = await requestJson(`/structure/find?q=${encodeURIComponent(String(fromStructureId))}`);
                const here = (found.structures || []).find((s) => sameId(s.id, fromStructureId));
                if (!here)
                    throw new Error("Couldn't load the node's current structure.");
                if (cancelled)
                    return;
                setCurrent(here);
                const params = new URLSearchParams({
                    easting: String(here.easting),
                    northing: String(here.northing),
                    radius: String(NEARBY_RADIUS),
                    limit: String(NEARBY_LIMIT),
                    exclude: String(here.id),
                });
                const near = await requestJson(`/structure/nearby?${params}`);
                if (!cancelled)
                    setNearby(near.structures || []);
            }
            catch (loadError) {
                if (!cancelled)
                    setError(loadError instanceof Error ? loadError.message : 'Could not load structures.');
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fromStructureId]);
    const choose = useCallback((structure) => {
        if (current && sameId(structure.id, current.id)) {
            setNotice("That's where the node is now.");
            return;
        }
        setNotice(null);
        setSelected(structure);
    }, [current]);
    // Map clicks pick the nearest node-hosting structure within SNAP_RADIUS.
    const handleMapPick = useCallback(async (latlng) => {
        try {
            const params = new URLSearchParams({
                lat: String(latlng.lat),
                lng: String(latlng.lng),
                radius: String(SNAP_RADIUS),
                limit: '1',
            });
            const result = await requestJson(`/structure/nearby?${params}`);
            const hit = (result.structures || [])[0];
            if (!hit) {
                setNotice('No structure there. Click closer to one.');
                return;
            }
            choose(hit);
        }
        catch (pickError) {
            setNotice(pickError instanceof Error ? pickError.message : 'Could not look up that point.');
        }
    }, [choose]);
    useStructurePick(map, Boolean(current) && !moved, handleMapPick);
    // Draw the current-structure ring, numbered pins and the dashed line.
    useEffect(() => {
        const L = window.L;
        if (!map || !L)
            return undefined;
        if (!layerRef.current)
            layerRef.current = L.layerGroup().addTo(map);
        const layer = layerRef.current;
        layer.clearLayers();
        if (!current || moved)
            return undefined;
        const here = L.latLng(current.lat, current.lng);
        L.circleMarker(here, {
            radius: 16, color: '#38bdf8', weight: 3, fill: false, interactive: false,
        }).addTo(layer);
        nearby.forEach((structure, index) => {
            const isSelected = selected && sameId(selected.id, structure.id);
            const isHovered = sameId(hoveredId, structure.id);
            const marker = L.marker([structure.lat, structure.lng], {
                icon: L.divIcon({
                    className: '',
                    html: `<div class="node-move-pin${isSelected ? ' node-move-pin--selected' : ''}${isHovered ? ' node-move-pin--hover' : ''}">${index + 1}</div>`,
                    iconSize: [24, 24],
                    iconAnchor: [12, 12],
                }),
                keyboard: false,
                zIndexOffset: isSelected || isHovered ? 1000 : 0,
            });
            marker.on('click', (event) => {
                L.DomEvent.stopPropagation(event);
                choose(structure);
            });
            marker.on('mouseover', () => setHoveredId(structure.id));
            marker.on('mouseout', () => setHoveredId(null));
            marker.addTo(layer);
        });
        if (selected) {
            const target = L.latLng(selected.lat, selected.lng);
            L.polyline([here, target], {
                color: '#38bdf8', weight: 3, dashArray: '6 6', interactive: false,
            }).addTo(layer);
            if (!nearby.some(s => sameId(s.id, selected.id))) {
                L.marker(target, {
                    icon: L.divIcon({
                        className: '',
                        html: '<div class="node-move-pin node-move-pin--selected">✓</div>',
                        iconSize: [24, 24],
                        iconAnchor: [12, 12],
                    }),
                    interactive: false,
                    zIndexOffset: 1000,
                }).addTo(layer);
            }
        }
        return undefined;
    }, [map, current, nearby, selected, hoveredId, moved, choose]);
    // Remove the drawing when the panel goes away.
    useEffect(() => () => {
        if (layerRef.current && map) {
            map.removeLayer(layerRef.current);
            layerRef.current = null;
        }
    }, [map]);
    // Esc cancels, unless a request is in flight.
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape' && !moving && undoState !== 'undoing')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [moving, undoState, onClose]);
    // Once moved, offer Undo for a few seconds, then close.
    useEffect(() => {
        if (!moved || undoState !== 'idle')
            return undefined;
        if (secondsLeft <= 0) {
            onClose();
            return undefined;
        }
        const timer = window.setTimeout(() => setSecondsLeft(s => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [moved, undoState, secondsLeft, onClose]);
    const handleFind = async (event) => {
        event.preventDefault();
        if (!query.trim())
            return;
        setFinding(true);
        setNotice(null);
        try {
            const params = new URLSearchParams({ q: query.trim() });
            if (current) {
                params.set('easting', String(current.easting));
                params.set('northing', String(current.northing));
            }
            const found = await requestJson(`/structure/find?${params}`);
            setResults(found.structures || []);
        }
        catch (findError) {
            setNotice(findError instanceof Error ? findError.message : 'Find failed.');
            setResults(null);
        }
        finally {
            setFinding(false);
        }
    };
    const chooseFound = (structure) => {
        if (current && sameId(structure.id, current.id)) {
            setNotice("That's where the node is now.");
            return;
        }
        choose(structure);
        map?.flyTo([structure.lat, structure.lng], Math.max(map.getZoom ? map.getZoom() : 18, 18));
    };
    const handleMove = async () => {
        if (!selected)
            return;
        setMoving(true);
        setError(null);
        try {
            const result = await moveNode(node.id, selected.id);
            setMoved({ fromId: String(result.from_structure_id), to: selected });
            setSecondsLeft(UNDO_SECONDS);
            onMoved?.();
        }
        catch (moveError) {
            setError(moveError instanceof Error ? moveError.message : 'The move failed.');
        }
        finally {
            setMoving(false);
        }
    };
    const handleUndo = async () => {
        if (!moved)
            return;
        setUndoState('undoing');
        try {
            await moveNode(node.id, moved.fromId);
            setUndoState('undone');
            onMoved?.();
            window.setTimeout(onClose, 2000);
        }
        catch (undoError) {
            setUndoState('idle');
            setError(undoError instanceof Error ? undoError.message : 'Undo failed.');
        }
    };
    // --- After the move: a compact notice with Undo --------------------------
    if (moved) {
        return (_jsxs("div", { className: "node-move-panel node-move-panel--notice", role: "status", children: [undoState === 'undone' ? (_jsxs("span", { children: ["Move undone. ", nodeName(node), " is back where it was."] })) : (_jsxs(_Fragment, { children: [_jsxs("span", { children: ["Moved ", nodeName(node), " to ", structureLabel(moved.to)] }), _jsx("button", { type: "button", className: "node-move-panel__undo", onClick: handleUndo, disabled: undoState === 'undoing', children: undoState === 'undoing' ? 'Undoing…' : `Undo (${secondsLeft})` })] })), error && _jsx("div", { className: "node-move-panel__error", children: error })] }));
    }
    const distance = current && selected ? gridDistance(current, selected) : null;
    return (_jsxs("div", { className: "node-move-panel", role: "dialog", "aria-labelledby": "node-move-panel-title", children: [_jsxs("div", { className: "node-move-panel__header", children: [_jsxs("h3", { id: "node-move-panel-title", children: ["Move ", nodeName(node)] }), _jsx("button", { type: "button", className: "node-move-panel__close", onClick: onClose, "aria-label": "Close", children: "\u00D7" })] }), _jsxs("div", { className: "node-move-panel__body", children: [_jsx("p", { className: "node-move-panel__lead", children: current
                            ? _jsxs(_Fragment, { children: ["Now on ", _jsx("strong", { children: structureLabel(current) }), ". Choose where it should go."] })
                            : 'Choose where it should go.' }), _jsxs("form", { className: "node-move-panel__find", onSubmit: handleFind, children: [_jsx("input", { type: "text", value: query, onChange: event => setQuery(event.target.value), placeholder: "Find: DP number, OBJECTID, chamber no., cabinet ID", "aria-label": "Find a structure" }), _jsx("button", { type: "submit", disabled: finding || !query.trim(), children: finding ? '…' : 'Find' })] }), results && (_jsx("div", { className: "node-move-panel__section", children: results.length === 0 ? (_jsxs("div", { className: "node-move-panel__empty", children: ["No structure matches '", query.trim(), "'."] })) : (_jsx("ul", { className: "node-move-panel__list", children: results.map(structure => (_jsx("li", { children: _jsxs("button", { type: "button", className: `node-move-panel__row${selected && sameId(selected.id, structure.id) ? ' is-selected' : ''}`, onClick: () => chooseFound(structure), children: [_jsx("span", { className: "node-move-panel__label", children: structureLabel(structure) }), _jsxs("span", { className: "node-move-panel__meta", children: [structureTypeLabel(structure.object_class), structure.distance !== null && structure.distance !== undefined
                                                    ? ` · ${formatDistance(structure.distance)}`
                                                    : ''] })] }) }, `find-${structure.id}`))) })) })), _jsxs("div", { className: "node-move-panel__section", children: [_jsx("div", { className: "node-move-panel__heading", children: "Nearby" }), loading && _jsx("div", { className: "node-move-panel__empty", children: "Loading nearby structures\u2026" }), !loading && current && nearby.length === 0 && (_jsxs("div", { className: "node-move-panel__empty", children: ["No structures within ", NEARBY_RADIUS, " m. Click one on the map or use Find."] })), _jsx("ul", { className: "node-move-panel__list", children: nearby.map((structure, index) => (_jsx("li", { children: _jsxs("button", { type: "button", className: `node-move-panel__row${selected && sameId(selected.id, structure.id) ? ' is-selected' : ''}`, onClick: () => choose(structure), onMouseEnter: () => setHoveredId(structure.id), onMouseLeave: () => setHoveredId(null), children: [_jsx("span", { className: "node-move-panel__badge", children: index + 1 }), _jsx("span", { className: "node-move-panel__label", children: structureLabel(structure) }), _jsxs("span", { className: "node-move-panel__meta", children: [structureTypeLabel(structure.object_class), " \u00B7 ", formatDistance(structure.distance), structure.node_count > 0
                                                        ? ` · ${structure.node_count} node${structure.node_count === 1 ? '' : 's'}`
                                                        : ''] })] }) }, structure.id))) }), current && (_jsx("div", { className: "node-move-panel__hint", children: "\u2026or click any structure on the map. Pan and zoom to reach one further away." }))] }), notice && _jsx("div", { className: "node-move-panel__notice", children: notice }), error && _jsx("div", { className: "node-move-panel__error", children: error })] }), _jsx("div", { className: "node-move-panel__footer", children: selected && distance !== null ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "node-move-panel__summary", children: ["Move to ", _jsx("strong", { children: structureLabel(selected) }), " \u00B7 ", formatDistance(distance), distance > FAR_WARNING_METRES && (_jsxs("div", { className: "node-move-panel__warning", children: ["This is ", formatDistance(distance), " away. Check it's the right structure."] }))] }), _jsx("button", { type: "button", className: "node-move-panel__move", onClick: handleMove, disabled: moving, children: moving ? 'Moving…' : 'Move' })] })) : (_jsx("div", { className: "node-move-panel__summary node-move-panel__summary--idle", children: "Select a structure to move the node to." })) })] }));
};
let closeActivePanel = null;
/**
 * Open the move panel in its own React root. Only one is open at a time.
 * Returns a function that closes it.
 */
export function openNodeMovePanel(props) {
    if (closeActivePanel)
        closeActivePanel();
    const container = document.createElement('div');
    container.className = 'node-move-panel-root';
    document.body.appendChild(container);
    const root = createRoot(container);
    const close = () => {
        if (closeActivePanel === close)
            closeActivePanel = null;
        // Defer: close can be called from inside this root's own event handlers.
        window.setTimeout(() => {
            root.unmount();
            container.remove();
        }, 0);
    };
    closeActivePanel = close;
    root.render(_jsx(NodeMovePanel, { ...props, onClose: close }));
    return close;
}
export default NodeMovePanel;
